import React from 'react';
import _ from 'lodash';
import classNames from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentLoader from 'react-content-loader';

import Config from 'arbitrip-common/client/utils/Config';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';
import PriceBreakdown from 'arbitrip-common/client/utils/PriceBreakdown';
import {
    getPointsDiscountValue,
    getMaxPointsForDeal,
    getPointsDebugTooltipContent,
    getTotalPriceInPoints,
} from 'arbitrip-common/client/utils/PointsUtils';

import Currencies from '@/utils/Currencies';
import ProfileStore from '@/stores/ProfileStore';
import SearchStore from '@/stores/SearchStore';
import { isAirHaifaCompany, isElalCompany, isIsracardCompany } from '@/utils/isCompany';
import DealPrice from '@/components/DealPrice.react';
import NegotiatedRate from '@/components/general/NegotiatedRate.react';
import DealPricePerNight from '@/components/pages/results/deal/DealPricePerNight/DealPricePerNight';
import DynamicPricingContainer from '@/components/pages/results/deal/DynamicPricingContainer';
import Constants from '@/components/pages/results/deal/constants';
import DealUtils from '@/entities/DealUtils';

const { NEWLINE } = Constants;
const SUPPLIER_EXPEDIA = 'E';
const POINTS_LOCALE = 'en-US';
const HTML_TOOLTIP_CLASSES = { tooltip: 'rm3-html-tooltip' };

/**
 * THE RM3 DEAL PRICE BLOCK — a branch-for-branch port of `DealComponent.react.js` (spec §5, Q20):
 * `DEAL_PRICE` (render), `getBreakdownPriceDescription`, `getDealsRender`, `getPriceDescription`,
 * `shouldDisplayPrice` and the travel-policy block, reading the same stores. `DealComponent` itself is
 * untouched; a branch that differs from it is a defect. The markup keeps today's class names and is rendered
 * inside a `.deals-wrapper .deal .deal-info` scope (`Rm3DealRow`), so `_dealComponent.scss` styles it as today.
 *
 * Not ported, because the RM3 surface never sets them: `display_price_without_recheck` and `representative`
 * (both default `false` on the hotel page's deal list), so `shouldDisplayPrice` reduces to its last two returns.
 */

function shouldDisplayPriceOf(deal) {
    if (deal.is_lazy_recheck) {
        return true;
    }
    return !!deal.chooseprod; // don't display price unless after recheck
}

export function isExpediaDealOf(deal) {
    return DealUtils.dealSupplierExtractor(deal).supplier === SUPPLIER_EXPEDIA;
}

export function nightsOf(deal) {
    const checkIn = DateUtils.parseFormatDate(deal.check_in);
    const checkOut = DateUtils.parseFormatDate(deal.check_out);
    return checkOut.diff(checkIn, 'days');
}

// A plain function, as legacy `getPriceDescription`: the Tooltip child must be a DOM element to take its ref.
function priceDescriptionOf(profile, text) {
    let className = 'tax-included';
    if (PriceBreakdown.isShowTotalPrice({ profile })) {
        className += ' tooltip-icon';
    }
    return <div className={className}>{text}</div>;
}

function DealsRender({ deal, profile }) {
    const { t } = useTranslation();
    const doesIncludeTax = deal && deal.does_price_include_tax;
    const isPostPay = deal.post_pay;
    const tooltipText = isPostPay ? t('PaymentHandledByProperty') : t('SpecialFees.Tooltip');

    return (
        <Tooltip title={tooltipText} placement="top" arrow>
            {isPostPay ? (
                priceDescriptionOf(profile, t('PayAtHotel'))
            ) : doesIncludeTax ? (
                priceDescriptionOf(profile, t('TaxIncluded'))
            ) : (
                <div className="tax-not-included">{t('MightNotIncludeTaxes')}</div>
            )}
        </Tooltip>
    );
}

function BreakdownPriceDescription({ deal, profile, nights, isPrivateTravelOnly, pointsDiscountValue }) {
    const { t } = useTranslation();
    const rooms = SearchStore.getRooms() || 1;

    if (isIsracardCompany() || isAirHaifaCompany()) {
        return (
            <DealPricePerNight
                deal={deal}
                profile={profile}
                nights={nights}
                expediaTotalPriceAfterDiscount={(deal.expedia_pricing?.totalPrice ?? 0) - pointsDiscountValue}
                totalPriceAfterDiscount={(deal.totalPrice ?? 0) - pointsDiscountValue}
                rooms={rooms}
                isPrivateTravelOnly={isPrivateTravelOnly}
            />
        );
    }

    const price = PriceBreakdown.isBreakdownPrice({ profile, deal })
        ? deal.expedia_pricing.totalPrice
        : deal.totalPrice;
    if (!price) return null;

    const totalPriceAfterDiscount = price - pointsDiscountValue;

    if (PriceBreakdown.isBreakdownPrice({ profile, deal }) || PriceBreakdown.isShowTotalPrice({ profile })) {
        const isElal = isElalCompany();

        return (
            <div className="tax-included align-right">
                {(isElal || totalPriceAfterDiscount > 0) && (
                    <div>
                        {nights > 1 && (
                            <div className="flex text-gap">
                                {!isElal && totalPriceAfterDiscount > 0 && (
                                    <span className="price">
                                        {Currencies.getPriceWithDisplayCurrencyByPrecision(
                                            totalPriceAfterDiscount / (nights || 1) / rooms,
                                            profile.display_currency_exchange_rate,
                                            profile.display_currency,
                                            0,
                                        )}
                                    </span>
                                )}
                                <span className="avg-night-text">
                                    {isElal ? (
                                        <Trans
                                            i18nKey="PriceForNight"
                                            count={nights}
                                            components={{ span: <span className="nights-bold bold" /> }}
                                        />
                                    ) : (
                                        t('AvgNight')
                                    )}
                                </span>
                            </div>
                        )}
                        {PriceBreakdown.isShowTotalPrice({ profile }) ? (
                            <DealsRender deal={deal} profile={profile} />
                        ) : (
                            <div>{t('IncludesTaxesAndFees')}</div>
                        )}
                    </div>
                )}
            </div>
        );
    }
    return null;
}

function travelPolicyTooltipOf(deal, travelPolicy, doesTravelPolicyExist, t) {
    const travelPolicyCurrency = doesTravelPolicyExist ? Currencies.getSymbol(travelPolicy.currency) : '';
    const travelPolicyLocation =
        travelPolicy && travelPolicy.default
            ? t('ThisDestination')
            : travelPolicy && travelPolicy.place
              ? travelPolicy.place.city || travelPolicy.place.state || travelPolicy.place.country
              : '';
    const travelPolicyText = doesTravelPolicyExist
        ? deal.out_of_policy_message ||
          t('Policy.MaxPriceAllowed', {
              location: travelPolicyLocation,
              price: travelPolicyCurrency + travelPolicy.price,
          })
        : null;

    const splitTravelPolicyText = travelPolicyText ? travelPolicyText.split(NEWLINE) : null;
    const dealIdPolicy = 'deal-' + deal.id + '-policy';
    return (
        <div className="policy-tooltip-container">
            {splitTravelPolicyText
                ? splitTravelPolicyText.map((line, idx) => <span key={dealIdPolicy + (idx + 1)}>{line}</span>)
                : null}
        </div>
    );
}

function Rm3DealPrice({ deal, profile, travelPolicy, arbitripPointsApplied, contractClientCompanyId, priceState }) {
    const { t } = useTranslation();
    const { points, showPricesWithPoints, anonPointsOnly, anonPointsExRate, busyApplyingPoints } = priceState;

    const faultDeal = deal && deal.faultDeal;
    const companyName = _.get(profile, 'company.name', null);

    const doesTravelPolicyExist = travelPolicy && (travelPolicy.default || travelPolicy.place);
    const isInPolicy = deal.in_policy;
    const tooltip = travelPolicyTooltipOf(deal, travelPolicy, doesTravelPolicyExist, t);

    const previousPrice =
        Config.dev_mode && deal.previousPricePerNight ? `the old price per night: ${deal.previousPricePerNight}` : '';
    const originalCurrencyPrice =
        Config.dev_mode && deal.currency !== profile.display_currency
            ? `the price in original currency: ${deal.currency}${deal.pricePerNight}`
            : '';
    const hasPriceChanged =
        Config.dev_mode && deal.previousPricePerNight && deal.previousPricePerNight !== deal.pricePerNight;

    const shouldDisplayPrice = shouldDisplayPriceOf(deal);
    const arbitripPointsReduction = arbitripPointsApplied ? _.get(profile, 'bank.balance', 0) : 0;
    const nights = nightsOf(deal);
    const isPrivateTravelOnly = !!_.get(profile, 'private_travel_only');
    const pointsDiscountValue = showPricesWithPoints ? getPointsDiscountValue(deal, profile, points) : 0;

    const isBreakdownPrice = PriceBreakdown.isBreakdownPrice({ profile, deal });
    const isShowTotalPrice = PriceBreakdown.isShowTotalPrice({ profile });

    let priceClasses = 'deal-price flex flex-column ';
    if (!isBreakdownPrice) {
        priceClasses += 'align-start';
    }

    let dealPriceClasses = 'deal-price-container flex flex-start ';
    if (!isBreakdownPrice) {
        dealPriceClasses += 'fixed-height';
    }

    const isExpediaDeal = isExpediaDealOf(deal);
    const isElal = isElalCompany();
    const breakdownDescription = (
        <BreakdownPriceDescription
            deal={deal}
            profile={profile}
            nights={nights}
            isPrivateTravelOnly={isPrivateTravelOnly}
            pointsDiscountValue={pointsDiscountValue}
        />
    );

    return (
        <div className={dealPriceClasses}>
            <div className="policy-average-wrapper flex flex-column align-flex-end">
                {isBreakdownPrice || isShowTotalPrice ? null : (
                    <div className="avg-room-night">
                        {isPrivateTravelOnly && nights > 1 ? (
                            <Trans
                                i18nKey="PriceForNight"
                                count={nights}
                                components={{ span: <span className="nights-bold bold" /> }}
                            />
                        ) : (
                            t('AvgRoomNight')
                        )}
                    </div>
                )}
                <div className="flex text-gap">
                    {shouldDisplayPrice && !isBreakdownPrice ? (
                        doesTravelPolicyExist ? (
                            <Tooltip title={tooltip} placement="top-end" arrow>
                                <div className="is-in-policy">
                                    <div className={isInPolicy ? 'in-policy icon' : 'out-of-policy icon'}></div>
                                    <div className={isInPolicy ? 'in-policy text' : 'out-of-policy text'}>
                                        <span>{isInPolicy ? t('InPolicy') : t('OutOfPolicy')}</span>
                                    </div>
                                </div>
                            </Tooltip>
                        ) : null
                    ) : null}
                    {isBreakdownPrice || isShowTotalPrice ? null : <DealsRender deal={deal} profile={profile} />}
                </div>
            </div>
            {busyApplyingPoints ? (
                <ContentLoader width={124} height={32} viewBox="0 0 160 32">
                    <rect x="0" y="0" rx="0" width="160" height="32" />
                </ContentLoader>
            ) : (
                <React.Fragment>
                    {ProfileStore.hasArbitripPoints() && arbitripPointsApplied && (
                        <div className="old-price">
                            <span>
                                {Currencies.getPriceWithDisplayCurrencyByPrecision(
                                    faultDeal ? deal.previousPricePerNight : deal.pricePerNight,
                                    profile.display_currency_exchange_rate,
                                    profile.display_currency,
                                    0,
                                )}
                            </span>
                        </div>
                    )}
                    <div className={priceClasses}>
                        <NegotiatedRate
                            companyName={companyName}
                            show={!!deal.negotiated_rate}
                            rawCode={deal.original_negotiated_rate || ''}
                            code={deal.negotiated_rate || ''}
                            codeDisplayName={deal.negotiated_rate_display_name || ''}
                        />
                        {(showPricesWithPoints || anonPointsOnly) && (
                            <>
                                {!anonPointsOnly && <div className="deal-points-text">{t('MaxPointsToRedeem')}</div>}
                                <div className="deal-points rm3-deal-points-row">
                                    <span className={`deal-points${isElal ? '-elal' : ''}`}>
                                        {anonPointsOnly
                                            ? getTotalPriceInPoints(deal, profile, anonPointsExRate).toLocaleString(
                                                  POINTS_LOCALE,
                                              )
                                            : getMaxPointsForDeal(deal, profile, points).toLocaleString(POINTS_LOCALE)}
                                    </span>
                                    <div className="deal-points__labels-container">
                                        <span className="deal-points-text">{t('Points')}</span>
                                        {Config.dev_mode && !anonPointsOnly && (
                                            <Tooltip
                                                title={getPointsDebugTooltipContent(
                                                    deal,
                                                    profile,
                                                    points,
                                                    pointsDiscountValue,
                                                    nights,
                                                    getMaxPointsForDeal,
                                                )}
                                                placement="top"
                                                arrow
                                            >
                                                <InfoOutlinedIcon className="rm3-points-debug-icon" />
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        {!anonPointsOnly && (
                            <div className="price-per-night">
                                {shouldDisplayPrice ? (
                                    isBreakdownPrice ? (
                                        <div>
                                            <div className="flex price-per-night-space">
                                                {showPricesWithPoints && <div className="price-per-night-plus">+</div>}
                                                <div className="deal-price">
                                                    {Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                                                        deal.expedia_pricing?.totalPrice - pointsDiscountValue,
                                                        profile.display_currency_exchange_rate,
                                                        profile.display_currency,
                                                    )}
                                                </div>
                                            </div>
                                            {breakdownDescription}
                                        </div>
                                    ) : isShowTotalPrice ? (
                                        <div>
                                            <div className="flex price-per-night-space">
                                                {showPricesWithPoints && <div className="price-per-night-plus">+</div>}
                                                <div className="deal-price">
                                                    {Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                                                        deal.totalPrice - pointsDiscountValue,
                                                        profile.display_currency_exchange_rate,
                                                        profile.display_currency,
                                                    )}
                                                </div>
                                            </div>
                                            {breakdownDescription}
                                        </div>
                                    ) : profile.agent ? (
                                        <div className="rm3-price-inline rm3-price-inline--agent">
                                            <DealPrice
                                                hasPriceChanged={hasPriceChanged}
                                                previousPrice={previousPrice}
                                                displayTotalPrice={true}
                                                supplier={deal.supplier_name}
                                                currency={deal.currency}
                                                price={deal.pricePerNight}
                                                totalPrice={deal.totalPrice}
                                                netPrice={deal.net_price_total}
                                                dynamicPricing={deal.dynamicPricing}
                                                commission={deal.commission_total}
                                                dealId={deal.id}
                                                faultDeal={faultDeal}
                                                profile={profile}
                                                contractClientCompanyId={contractClientCompanyId}
                                            />
                                        </div>
                                    ) : (
                                        <div className="rm3-price-inline">
                                            <span
                                                title={
                                                    Config.dev_mode &&
                                                    (previousPrice + (deal.currency !== profile.display_currency)
                                                        ? ' | ' + originalCurrencyPrice
                                                        : '')
                                                }
                                                className={classNames(faultDeal && 'fault-deal')}
                                            >
                                                <span
                                                    data-testid="deal-price"
                                                    data-deal-price-safe-for-test={isExpediaDeal}
                                                    className="deal-price"
                                                >
                                                    {showPricesWithPoints && (
                                                        <span className="price-per-night-plus">+</span>
                                                    )}
                                                    {Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                                                        isPrivateTravelOnly && nights > 1
                                                            ? deal.totalPrice - pointsDiscountValue
                                                            : (faultDeal
                                                                  ? deal.previousPricePerNight
                                                                  : deal.pricePerNight) -
                                                                  arbitripPointsReduction -
                                                                  (nights > 0 ? pointsDiscountValue / nights : 0),
                                                        profile.display_currency_exchange_rate,
                                                        profile.display_currency,
                                                    )}
                                                </span>
                                            </span>
                                            <DynamicPricingContainer
                                                dynamicPricing={deal.dynamicPricing}
                                                profile={profile}
                                                currency={deal.currency}
                                            />
                                        </div>
                                    )
                                ) : null}
                            </div>
                        )}
                    </div>

                    {shouldDisplayPrice && ProfileStore.isLeisureExperienceEnabled() && arbitripPointsReduction > 0 ? (
                        <Tooltip title={t('YourPointsAreApplied')} placement="top" classes={HTML_TOOLTIP_CLASSES}>
                            <InfoOutlinedIcon className="rm3-points-applied-icon" />
                        </Tooltip>
                    ) : null}
                </React.Fragment>
            )}
        </div>
    );
}

export default Rm3DealPrice;
